import { useEffect, useState } from "react";
import { PreferredMinistry } from "@/components/PreferredMinistry";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Download, Loader2 } from "lucide-react";
import { api, API } from "@/lib/api";
import { ResultView } from "@/components/ResultView";
import { t, LangToggle } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";
import { AiSummary } from "@/components/AiSummary";
import { AiCoach } from "@/components/AiCoach";
import { useStudioTheme, resolvePublicTheme } from "@/lib/theme";
import { DtMark } from "@/components/DtMark";

const FreeUpsell = ({ lang }) => (
  <section className="bg-navy text-white rounded-2xl p-8 md:p-10 flex flex-wrap items-center justify-between gap-6" data-testid="free-upsell">
    <div className="max-w-xl">
      <p className="eyebrow !text-[#8FD3C6]">{lang === "es" ? "Siguiente paso" : "Next step"}</p>
      <h2 className="font-display text-3xl font-semibold mt-2">{lang === "es" ? "Descubre más sobre cómo fuiste diseñado para servir" : "Discover More About How You're Wired to Serve"}</h2>
      <p className="text-white/75 mt-3">{lang === "es" ? "Ya descubriste tus fortalezas de personalidad. ¿Listo para explorar tus dones espirituales y encontrar dónde puedes tener el mayor impacto?" : "You've discovered your personality strengths. Ready to explore your spiritual gifts and find where you can make the greatest impact?"}</p>
    </div>
    <Link to="/#pricing" className="btn-ghost !text-navy" data-testid="free-upsell-btn">{lang === "es" ? "Explorar Dream Teams" : "Explore Dream Teams"}</Link>
  </section>
);

export default function PublicResult() {
  const { vid } = useParams();
  const [params, setParams] = useSearchParams();
  const [v, setV] = useState(null);
  const [ref, setRef] = useState(null);
  const [err, setErr] = useState(false);
  const qLang = params.get("lang");
  useStudioTheme(!!v && !v.free && resolvePublicTheme(params, v.church?.theme) === "studio");

  useEffect(() => {
    api.get(`/public/results/${vid}`, { params: qLang ? { lang: qLang } : {} })
      .then((r) => { setV(r.data); return api.get("/reference", { params: { lang: r.data.lang } }); })
      .then((r) => setRef(r.data))
      .catch(() => setErr(true));
  }, [vid, qLang]);

  const lang = v?.lang || qLang || "en";
  if (err) return <div className="min-h-screen flex items-center justify-center font-display text-2xl text-navy">{t(lang, "notFound")}</div>;
  if (!v) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-teal" /></div>;

  return (
    <div className="min-h-screen bg-paper">
      <header className="assess-header result-header bg-white border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="flex items-center gap-3 font-display font-semibold text-navy min-w-0">{v.church?.logo_file_id && <img src={mediaUrl(v.church.logo_file_id)} alt="" className="h-8 w-auto" data-testid="church-logo" />}<span className="truncate">{v.church?.name}</span></span>
          <span className="flex items-center gap-3 sm:gap-4 shrink-0">
            {(v.pro || v.free) && <LangToggle lang={lang} onChange={(l) => setParams((p) => { p.set("lang", l); return p; })} />}
            <DtMark />
          </span>
        </div>
      </header>
      <main className="studio-main result-main max-w-6xl mx-auto px-6 py-14 fade-up">
        <p className="eyebrow">{t(lang, "yourResults")}</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mt-3">
          <h1 className="font-display text-navy text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="result-heading">
            {t(lang, "heresWhere", { name: v.name.split(" ")[0] })} <span className="font-accent text-teal">{v.disc_only ? (lang === "es" ? "cómo fuiste diseñado." : "how you're wired.") : t(lang, "thrive")}</span>
          </h1>
          {(v.paid || v.free) && <a href={`${API}/public/results/${vid}/pdf?lang=${lang}`} className="btn-navy flex items-center gap-2" data-testid="download-pdf-btn">
            <Download size={18} /> {t(lang, "download")}
          </a>}
        </div>
        <p className="text-muted-ink text-lg mt-4 mb-10 max-w-2xl">
          {v.shared ? t(lang, "shared", { church: v.church?.name }) : t(lang, "bookmark")}
        </p>
        <div className="space-y-6">
          {v.pro && <AiSummary vid={vid} lang={lang} />}
          <ResultView v={v} reference={ref} lang={lang} />
          {!v.free && v.team_options?.length > 0 && <PreferredMinistry vid={vid} options={v.team_options} initial={v.preferred?.team_id} lang={lang} />}
          {v.free ? <FreeUpsell lang={lang} /> : v.pro && <AiCoach vid={vid} lang={lang} />}
        </div>
      </main>
    </div>
  );
}
